import React, { useCallback, useEffect, useState } from "react";
import {
  DatabaseBackup, Download, Loader2, RotateCcw, Trash2, Cloud, HardDrive, Search, X, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { confirmAsync } from "@/lib/safeConfirm";

const KIND_LABEL = {
  manual: { text: "Manual", cls: "bg-clay-900 text-white" },
  daily: { text: "Diaria", cls: "bg-clay-200 text-clay-900" },
  deploy: { text: "Pre-actualización", cls: "bg-terracotta text-white" },
  "pre-restore": { text: "Pre-restauración", cls: "bg-amber-200 text-amber-900" },
};

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  const rel = mins < 60 ? `hace ${Math.max(mins, 0)} min`
    : mins < 1440 ? `hace ${Math.round(mins / 60)} h`
      : `hace ${Math.round(mins / 1440)} d`;
  return `${d.toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} · ${rel}`;
};

export default function Backups() {
  const [items, setItems] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [note, setNote] = useState("");
  const [tripModal, setTripModal] = useState(null);
  const [fullModal, setFullModal] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/backups");
      setItems(data.backups || []);
      setPolicy(data.policy || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar las copias");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/backups", { note });
      toast.success(`Copia creada · ${data.total_docs} registros · ${fmtSize(data.size_bytes)}`);
      setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando la copia");
    } finally {
      setCreating(false);
    }
  };

  const download = async (id) => {
    try {
      const res = await api.get(`/backups/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${id}.json.gz`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo descargar");
    }
  };

  const remove = async (id) => {
    if (!(await confirmAsync("¿Eliminar esta copia de seguridad?", { destructive: true, confirmLabel: "Eliminar" }))) return;
    try { await api.delete(`/backups/${id}`); load(); }
    catch (e) { toast.error("No se pudo eliminar"); }
  };

  const last = items[0];

  return (
    <div className="px-8 py-8 max-w-6xl" data-testid="backups-page">
      <div className="mb-6">
        <div className="smallcaps">Administración</div>
        <h1 className="font-serif text-5xl leading-none mt-3">Copias de seguridad</h1>
        <p className="text-sm text-clay-700 mt-3 max-w-2xl">
          Cada copia guarda <strong>todo el negocio</strong> (itinerarios y archivados, curated trips y sus
          reservas, experiencias, hoteles, proveedores, ejemplos del entrenador y accesos) en un único fichero
          comprimido, por duplicado: en la base de datos y en la nube de Emergent.
          {policy && (
            <> Se hace una <strong>de lunes a viernes</strong> a las 03:00 y otra en cada despliegue; las de más
            de <strong>{policy.retention_days} días</strong> se borran solas (siempre se conservan las {policy.min_keep} últimas).</>
          )}
        </p>
      </div>

      <div className="grid md:grid-cols-[1fr_auto] gap-4 items-end border border-clay-300 bg-white p-5 mb-6">
        <div>
          <div className="smallcaps mb-2">Crear copia ahora</div>
          <input
            data-testid="backup-note-input"
            placeholder="Nota opcional — p. ej. «antes de subir la mejora de fotos»"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full bg-white border border-clay-300 px-3 py-2 text-sm outline-none focus:border-terracotta"
          />
        </div>
        <button
          data-testid="backup-create-btn"
          onClick={create}
          disabled={creating}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-clay-900 text-white text-sm tracking-wider uppercase hover:bg-terracotta disabled:opacity-50"
        >
          {creating ? <Loader2 size={14} className="animate-spin"/> : <DatabaseBackup size={14}/>}
          {creating ? "Creando…" : "Crear copia"}
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <Stat label="Última copia" value={last ? fmtWhen(last.created_at) : "Ninguna todavía"} testid="stat-last"/>
        <Stat label="Copias guardadas" value={items.length} testid="stat-count"/>
        <Stat
          label="Automatismo"
          value={policy
            ? `L-V 03:00 + cada despliegue · se borran a los ${policy.retention_days} días`
            : "Diaria L-V 03:00 + cada despliegue"}
          testid="stat-schedule"
        />
      </div>

      <div className="smallcaps mb-3">Histórico</div>
      <div className="border border-clay-300 bg-white" data-testid="backups-list">
        {loading ? (
          <div className="p-6 text-sm text-clay-700 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-clay-700">Todavía no hay copias. Crea la primera antes de publicar.</div>
        ) : items.map((b) => {
          const kind = KIND_LABEL[b.kind] || { text: b.kind, cls: "bg-clay-200 text-clay-900" };
          return (
            <div key={b.backup_id} className="px-4 py-3 border-t first:border-t-0 border-clay-300 flex items-center gap-4 flex-wrap" data-testid={`backup-row-${b.backup_id}`}>
              <div className="min-w-[230px]">
                <div className="text-sm font-semibold">{fmtWhen(b.created_at)}</div>
                <div className="text-[11px] text-clay-700 truncate max-w-xs">{b.note || b.created_by}</div>
              </div>
              <span className={`text-[10px] tracking-widest uppercase px-2 py-1 ${kind.cls}`}>{kind.text}</span>
              <div className="text-sm text-clay-700">
                <strong>{b.counts?.itineraries ?? 0}</strong> itinerarios · {b.total_docs} registros · {fmtSize(b.size_bytes || 0)}
              </div>
              <div className="flex items-center gap-1.5 text-clay-700" title={`Guardada en: ${(b.stored_in || []).join(" + ")}`}>
                {(b.stored_in || []).includes("mongo") && <HardDrive size={14}/>}
                {(b.stored_in || []).includes("cloud") ? <Cloud size={14}/> : <Cloud size={14} className="opacity-25"/>}
              </div>
              <div className="ml-auto flex items-center gap-1">
                <button data-testid={`backup-download-${b.backup_id}`} onClick={() => download(b.backup_id)} className="p-1.5 hover:bg-clay-200" title="Descargar"><Download size={14}/></button>
                <button data-testid={`backup-restore-trip-${b.backup_id}`} onClick={() => setTripModal(b)} className="px-2.5 py-1.5 text-xs border border-clay-300 hover:bg-clay-100">Restaurar viaje</button>
                <button data-testid={`backup-restore-full-${b.backup_id}`} onClick={() => setFullModal(b)} className="px-2.5 py-1.5 text-xs border border-clay-300 hover:bg-clay-100">Restaurar todo</button>
                <button data-testid={`backup-delete-${b.backup_id}`} onClick={() => remove(b.backup_id)} className="p-1.5 hover:bg-clay-200 text-destructive" title="Eliminar"><Trash2 size={14}/></button>
              </div>
            </div>
          );
        })}
      </div>

      {tripModal && <RestoreTripModal backup={tripModal} onClose={() => setTripModal(null)} onDone={load}/>}
      {fullModal && <RestoreFullModal backup={fullModal} onClose={() => setFullModal(null)} onDone={load}/>}
    </div>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="border border-clay-300 bg-white px-4 py-3" data-testid={testid}>
    <div className="smallcaps">{label}</div>
    <div className="text-sm mt-1">{value}</div>
  </div>
);

const Modal = ({ title, subtitle, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-6 overflow-auto" data-testid={testid}>
    <div className="bg-white border border-clay-300 w-full max-w-3xl mt-10">
      <div className="px-6 py-4 border-b border-clay-300 flex items-start justify-between gap-4">
        <div>
          <div className="font-serif text-2xl">{title}</div>
          {subtitle && <div className="text-[11px] text-clay-700 mt-1">{subtitle}</div>}
        </div>
        <button onClick={onClose} data-testid="modal-close" className="p-1.5 hover:bg-clay-200"><X size={16}/></button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

function RestoreTripModal({ backup, onClose, onDone }) {
  const [trips, setTrips] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/backups/${backup.backup_id}/itineraries`)
      .then(({ data }) => setTrips(data.itineraries || []))
      .catch(() => { toast.error("No se pudo leer la copia"); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pick = async (t) => {
    setSelected(t); setPreview(null);
    try {
      const { data } = await api.get(`/backups/${backup.backup_id}/itineraries/${t.itinerary_id}/preview`);
      setPreview(data);
    } catch { toast.error("No se pudo comparar"); }
  };

  const restore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/backups/${backup.backup_id}/restore-itinerary/${selected.itinerary_id}`);
      toast.success(`«${data.name}» restaurado. Se guardó una copia previa por si acaso.`);
      onDone(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo restaurar");
    } finally { setBusy(false); }
  };

  const list = (trips || []).filter((t) => !q || (t.name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <Modal
      title="Restaurar un viaje"
      subtitle={`Desde la copia del ${fmtWhen(backup.created_at)}`}
      onClose={onClose}
      testid="restore-trip-modal"
    >
      {!trips ? (
        <div className="text-sm text-clay-700 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Leyendo la copia…</div>
      ) : (
        <>
          <div className="flex items-center gap-2 border border-clay-300 px-3 py-2 mb-3">
            <Search size={14} className="text-clay-700"/>
            <input data-testid="restore-trip-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar viaje…" className="flex-1 text-sm outline-none"/>
          </div>
          <div className="border border-clay-300 max-h-64 overflow-auto">
            {list.map((t) => (
              <button
                key={t.itinerary_id}
                data-testid={`restore-pick-${t.itinerary_id}`}
                onClick={() => pick(t)}
                className={`w-full text-left px-3 py-2 border-t first:border-t-0 border-clay-300 text-sm hover:bg-clay-100 ${selected?.itinerary_id === t.itinerary_id ? "bg-clay-100" : ""}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold truncate">{t.name || t.itinerary_id}</span>
                  <span className="text-[11px] text-clay-700 shrink-0">
                    {t.days} días · {t.status}{t.exists_now ? "" : " · ya no existe"}
                  </span>
                </div>
              </button>
            ))}
            {list.length === 0 && <div className="px-3 py-3 text-sm text-clay-700">Sin resultados.</div>}
          </div>

          {selected && (
            <div className="mt-5 border border-clay-300 p-4" data-testid="restore-trip-preview">
              <div className="smallcaps mb-3">Qué va a cambiar</div>
              {!preview ? <Loader2 size={14} className="animate-spin"/> : (
                <>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <Column title="Ahora" s={preview.current} empty="Este viaje ya no existe — se volverá a crear"/>
                    <Column title="En la copia" s={preview.backup}/>
                  </div>
                  {preview.changed_fields?.length > 0 && (
                    <div className="mt-3 text-[11px] text-clay-700">
                      Campos que cambian: {preview.changed_fields.slice(0, 12).join(", ")}
                      {preview.changed_fields.length > 12 ? ` y ${preview.changed_fields.length - 12} más` : ""}
                    </div>
                  )}
                  {preview.exists_now && preview.changed_fields?.length === 0 && (
                    <div className="mt-3 text-[11px] text-clay-700">Idéntico a la versión actual: restaurar no cambiaría nada.</div>
                  )}
                </>
              )}
              <button
                data-testid="restore-trip-confirm"
                onClick={restore}
                disabled={busy || !preview}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-clay-900 text-white text-sm tracking-wider uppercase hover:bg-terracotta disabled:opacity-50"
              >
                {busy ? <Loader2 size={14} className="animate-spin"/> : <RotateCcw size={14}/>} Restaurar este viaje
              </button>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

const Column = ({ title, s, empty }) => (
  <div>
    <div className="text-[11px] uppercase tracking-widest text-clay-700 mb-1">{title}</div>
    {!s ? <div className="text-clay-700">{empty || "—"}</div> : (
      <ul className="space-y-0.5">
        <li className="font-semibold truncate">{s.name}</li>
        <li>{s.days} días · {s.services} servicios</li>
        <li>{s.accommodations} hoteles · {s.extras} extras · {s.payments} pagos</li>
        <li className="text-[11px] text-clay-700">Estado: {s.status} · guardado {fmtWhen(s.updated_at)}</li>
      </ul>
    )}
  </div>
);

function RestoreFullModal({ backup, onClose, onDone }) {
  const [diff, setDiff] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/backups/${backup.backup_id}/preview`)
      .then(({ data }) => setDiff(data.diff))
      .catch(() => { toast.error("No se pudo leer la copia"); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/backups/${backup.backup_id}/restore-full`, { confirm });
      const total = Object.values(data.restored).reduce((a, r) => a + r.updated + r.recreated, 0);
      toast.success(`Restauración completa: ${total} registros. Copia previa guardada.`);
      onDone(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo restaurar");
    } finally { setBusy(false); }
  };

  const rows = Object.entries(diff || {}).filter(([, v]) => v.in_backup || v.now);

  return (
    <Modal
      title="Restauración total"
      subtitle={`Desde la copia del ${fmtWhen(backup.created_at)}`}
      onClose={onClose}
      testid="restore-full-modal"
    >
      <div className="flex items-start gap-3 border border-amber-300 bg-amber-50 p-3 mb-4 text-sm">
        <ShieldAlert size={16} className="text-amber-700 mt-0.5 shrink-0"/>
        <div>
          Se sobrescriben los registros que existan en la copia. <strong>Nada se borra</strong>: lo creado
          después de la copia se queda como está. Antes de empezar se guarda automáticamente una copia del
          estado actual.
        </div>
      </div>

      {!diff ? (
        <div className="text-sm text-clay-700 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Comparando…</div>
      ) : (
        <>
          <div className="border border-clay-300" data-testid="restore-full-diff">
            <div className="grid grid-cols-[1fr_repeat(4,80px)] gap-2 px-3 py-2 text-[10px] uppercase tracking-widest text-clay-700 border-b border-clay-300">
              <span>Colección</span><span className="text-right">En copia</span><span className="text-right">Cambian</span><span className="text-right">Se recrean</span><span className="text-right">Se conservan</span>
            </div>
            {rows.map(([name, v]) => (
              <div key={name} className="grid grid-cols-[1fr_repeat(4,80px)] gap-2 px-3 py-2 text-sm border-t border-clay-300 first:border-t-0">
                <span>{name}</span>
                <span className="text-right">{v.in_backup}</span>
                <span className={`text-right ${v.to_update ? "font-semibold text-terracotta" : "text-clay-700"}`}>{v.to_update}</span>
                <span className={`text-right ${v.to_recreate ? "font-semibold" : "text-clay-700"}`}>{v.to_recreate}</span>
                <span className="text-right text-clay-700">{v.extra_now}</span>
              </div>
            ))}
          </div>

          <div className="mt-5">
            <div className="smallcaps mb-2">Escribe RESTAURAR para confirmar</div>
            <div className="flex gap-2">
              <input
                data-testid="restore-full-confirm-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="RESTAURAR"
                className="flex-1 border border-clay-300 px-3 py-2 text-sm outline-none focus:border-terracotta"
              />
              <button
                data-testid="restore-full-confirm"
                onClick={run}
                disabled={busy || confirm.trim().toUpperCase() !== "RESTAURAR"}
                className="inline-flex items-center gap-2 px-4 py-2 bg-destructive text-white text-sm tracking-wider uppercase disabled:opacity-40"
              >
                {busy ? <Loader2 size={14} className="animate-spin"/> : <RotateCcw size={14}/>} Restaurar todo
              </button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
